import { AgGridAngular } from "@ag-grid-community/angular";
import { ColDef, ColumnRowGroupChangedEvent, GridOptions } from "@ag-grid-community/core";
import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { PARENT_ASIN_COL_NAME } from "@front/m19-grid-config";
import { Metric } from "@front/m19-metrics";
import { AdStatsEx, CurrencyStat, OrderStats, StrategyEx } from "@front/m19-models";
import { MetricsSelectorKey, UserSelectionService } from "@front/m19-services";
import {
  ChartConfig,
  IButtonDirective,
  ICardComponent,
  ICardContent,
  ICardHeader,
  IChartComponent,
  IDateAggreationSwitchComponent,
  IIconComponent,
  IToggleComponent,
  ITooltipDirective,
  Option,
} from "@front/m19-ui";
import { DateAggregation } from "@front/m19-utils";
import { TranslocoModule } from "@jsverse/transloco";
import { ActivityEventFiltersComponent } from "@m19-board/activity-event-filters.component";
import { restoreDefaultColumns } from "@m19-board/grid-config/grid-config";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { CompactFilterComponent, Filter } from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilter } from "@m19-board/shared/filter/asin-filter.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { SpinnerComponent } from "@front/m19-ui";
import { Observable } from "rxjs";
import { ActivityEventAnnotationsService, ActivityEventType } from "../activities/activity-event-annotations.service";

@Component({
  selector: "app-stats",
  imports: [
    CommonModule,
    ActivityEventFiltersComponent,
    AgGridAngular,
    ExportButtonComponent,
    IButtonDirective,
    ITooltipDirective,
    MetricSelectorComponent,
    TranslocoModule,
    ICardComponent,
    SpinnerComponent,
    ICardHeader,
    ICardContent,
    IDateAggreationSwitchComponent,
    CompactFilterComponent,
    IToggleComponent,
    IIconComponent,
    IChartComponent,
  ],
  templateUrl: "./stats.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsComponent<T> implements OnInit, AfterViewInit {
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly userSelectionService = inject(UserSelectionService);

  protected allStrategies$: Observable<Option<StrategyEx>[]> =
    this.activityEventAnnotationsService.allStrategiesOptions$;
  protected allEventAnnotationTypes: Option<ActivityEventType>[] =
    this.activityEventAnnotationsService.allActivityEventTypesOptions;

  metricsConfig = input.required<StatsComponentMetricConfig<T>>();
  gridConfig = input.required<StatsComponentGridConfig<T>>();
  chartConfig = input.required<ChartConfig<CurrencyStat, "line">>();
  chartData = input.required<CurrencyStat[]>();

  filters = input<Filter<AsinFilter>[] | undefined>(undefined);
  dateAggregation = input.required<DateAggregation>();
  withDateAggregation = input<boolean>(true);
  localStorageKey = input.required<MetricsSelectorKey>();
  dataLoading = input<boolean>(false);
  withEventAnnotation = input<boolean>(false);
  displayEventAnnotation = input<boolean>(false);

  _grid = viewChild.required<AgGridAngular>("grid");

  isGlobalChartHidden = signal(false);
  isGroupByParent = signal(false);
  disableEventAnnotation = signal(false);

  metricsSelect = output<Metric<AdStatsEx>[]>();
  filterChange = output<Filter<AsinFilter>[]>();
  dateAggregationChange = output<DateAggregation>();
  displayEventAnnotationChange = output<boolean>();
  grid = output<AgGridAngular>();

  @Output()
  csvExport = new EventEmitter<void>();

  ngOnInit() {
    this.isGlobalChartHidden.set(!this.userSelectionService.getUserChartDisplayedPreference(this.localStorageKey()));
  }

  ngAfterViewInit() {
    this.grid.emit(this._grid());
  }

  applyFilter(filters: Filter<AsinFilter>[]) {
    this.filterChange.emit(filters);
  }

  selectMetrics(metrics: Metric<AdStatsEx>[]) {
    this.metricsSelect.emit([...metrics]);
  }

  toggleEventAnnotationDisplay(checked: boolean) {
    this.displayEventAnnotationChange.emit(checked);
  }

  changeDateAggregation(dateAggregation: DateAggregation) {
    this.disableEventAnnotation.set(dateAggregation !== DateAggregation.daily);
    this.dateAggregationChange.emit(dateAggregation);
  }

  toggleGlobalChartDisplay(hide: boolean) {
    this.isGlobalChartHidden.set(hide);
    this.userSelectionService.setUserChartDisplayedPreference(this.localStorageKey(), !this.isGlobalChartHidden());
  }

  toggleParentAsin() {
    this.isGroupByParent.update((groupByParent) => !groupByParent);
    this._grid().api.setRowGroupColumns(this.isGroupByParent() ? [PARENT_ASIN_COL_NAME] : []);
  }

  restoreDefaultColumns() {
    restoreDefaultColumns(this.localStorageKey().page, this._grid().api);
  }

  onCsvExport() {
    this.csvExport.emit();
  }

  onColumnRowGroupChanged(event: ColumnRowGroupChangedEvent<OrderStats>) {
    this.isGroupByParent.set(!!(event.column?.isRowGroupActive() && event.column.getColId() === PARENT_ASIN_COL_NAME));
  }
}

export interface StatsComponentMetricConfig<T> {
  data?: CurrencyStat;
  previousData?: CurrencyStat;
  metrics: Metric<AdStatsEx>[];
}

export interface StatsComponentGridConfig<T> {
  columnDefs: ColDef[];
  gridData?: T[];
  gridOptions: GridOptions;
}
